import { useEffect, useState, type RefObject } from 'react';
import { AppScreen } from './AppScreen';
import { ThemeToggle } from './ThemeToggle';
import { SearchIcon, UserIcon, UsersIcon, LinkIcon, EyeIcon } from './icons';

interface LandingScreenProps {
  playerName: string;
  onPlayerNameChange: (value: string) => void;
  nameInputRef: RefObject<HTMLInputElement>;
  onNameEnter: () => void;
  pendingJoinCode: string | null;
  isDark: boolean;
  onToggleTheme: () => void;
  landingMode: 'idle' | 'join';
  onShowJoin: () => void;
  onBackFromJoin: () => void;
  joinError: string | null;
  onClearJoinError: () => void;
  onSelectSingle: () => void;
  onSelectMultiplayer: () => void;
  onSubmitJoin: (code: string) => void;
  onDemo: () => void;
}

export function LandingScreen({
  playerName,
  onPlayerNameChange,
  nameInputRef,
  onNameEnter,
  pendingJoinCode,
  isDark,
  onToggleTheme,
  landingMode,
  onShowJoin,
  onBackFromJoin,
  joinError,
  onClearJoinError,
  onSelectSingle,
  onSelectMultiplayer,
  onSubmitJoin,
  onDemo,
}: LandingScreenProps) {
  const [joinCode, setJoinCode] = useState(pendingJoinCode ?? '');

  useEffect(() => {
    if (pendingJoinCode) setJoinCode(pendingJoinCode);
  }, [pendingJoinCode]);

  return (
    <AppScreen>
      <div className="sticky top-0 z-10 bg-atlas-bg dark:bg-atlas-bg-dark shrink-0 flex justify-end pt-[calc(env(safe-area-inset-top)+8px)] px-4">
        <ThemeToggle isDark={isDark} onToggle={onToggleTheme} />
      </div>

      <div className="text-center px-6 pt-4">
        <SearchIcon className="w-12 h-12 mx-auto mb-3 text-atlas-brass dark:text-atlas-brass-dark" />
        <h1 className="font-serif text-4xl font-semibold text-atlas-ink dark:text-atlas-ink-dark tracking-tight">
          Alphabet Hunter
        </h1>
        <p className="mt-2 text-atlas-ink-soft dark:text-atlas-ink-soft-dark text-sm max-w-[280px] mx-auto">
          The road sign game — spot a word for every letter A to Z
        </p>
      </div>

      {pendingJoinCode && (
        <div className="mt-4 mx-6 p-4 rounded-xl border border-dashed border-atlas-brass dark:border-atlas-brass-dark bg-atlas-surface dark:bg-atlas-surface-dark flex flex-col gap-1 text-center">
          <span className="text-sm font-semibold text-atlas-ink dark:text-atlas-ink-dark">
            You have been invited to join a game!
          </span>
          <span className="text-xs text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
            Enter your name below, then tap Join Game.
          </span>
        </div>
      )}

      <div className="flex-1 flex flex-col justify-center px-6 py-6 gap-3.5 w-full">
        <div>
          <label className="block text-sm font-medium text-atlas-ink-soft dark:text-atlas-ink-soft-dark mb-1">
            Your Name
          </label>
          <input
            type="text"
            ref={nameInputRef}
            value={playerName}
            onChange={(e) => onPlayerNameChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onNameEnter();
            }}
            placeholder="Enter your name…"
            maxLength={20}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="words"
            spellCheck={false}
            className="w-full px-4 py-3 rounded-xl border border-atlas-rule dark:border-atlas-rule-dark bg-atlas-surface dark:bg-atlas-surface-dark text-atlas-ink dark:text-atlas-ink-dark placeholder-atlas-ink-soft/60 dark:placeholder-atlas-ink-soft-dark/60 focus:outline-none focus:ring-2 focus:ring-atlas-brass dark:focus:ring-atlas-brass-dark text-base"
          />
        </div>

        {landingMode === 'idle' ? (
          <>
            <button
              onClick={onSelectSingle}
              className="w-full h-[52px] rounded-xl bg-atlas-brass dark:bg-atlas-brass-dark hover:brightness-95 active:brightness-90 text-atlas-ink font-semibold text-[17px] transition-all flex items-center justify-center gap-2.5"
            >
              <UserIcon className="w-5 h-5" />
              Single Player
            </button>
            <button
              onClick={onSelectMultiplayer}
              className="w-full h-[52px] rounded-xl border-[1.5px] border-atlas-ink dark:border-atlas-ink-dark text-atlas-ink dark:text-atlas-ink-dark font-semibold text-[17px] transition-colors hover:bg-atlas-surface dark:hover:bg-atlas-surface-dark flex items-center justify-center gap-2.5"
            >
              <UsersIcon className="w-5 h-5" />
              Multiplayer
            </button>
            <button
              onClick={onShowJoin}
              className="w-full h-[52px] rounded-xl border-[1.5px] border-atlas-ink dark:border-atlas-ink-dark text-atlas-ink dark:text-atlas-ink-dark font-semibold text-[17px] transition-colors hover:bg-atlas-surface dark:hover:bg-atlas-surface-dark flex items-center justify-center gap-2.5"
            >
              <LinkIcon className="w-5 h-5" />
              Join a Game
            </button>

            <div className="mt-2 p-4 rounded-xl border border-dashed border-atlas-rule dark:border-atlas-rule-dark bg-atlas-surface dark:bg-atlas-surface-dark flex flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-atlas-ink-soft dark:text-atlas-ink-soft-dark">
                No one to play with?
              </span>
              <button
                onClick={onDemo}
                className="text-left text-[15px] font-semibold text-atlas-brass dark:text-atlas-brass-dark hover:text-atlas-brass/80 dark:hover:text-atlas-brass-dark/80 flex items-center gap-1.5"
              >
                <EyeIcon className="w-4 h-4" />
                Try a Demo Game →
              </button>
            </div>
          </>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-atlas-ink-soft dark:text-atlas-ink-soft-dark mb-1">
                Game Code
              </label>
              <input
                type="text"
                value={joinCode}
                onChange={(e) => {
                  setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6));
                  onClearJoinError();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSubmitJoin(joinCode);
                }}
                placeholder="ABC123"
                maxLength={6}
                autoFocus
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="characters"
                spellCheck={false}
                className="w-full px-4 py-3 rounded-xl border border-atlas-rule dark:border-atlas-rule-dark bg-atlas-surface dark:bg-atlas-surface-dark text-atlas-ink dark:text-atlas-ink-dark placeholder-atlas-ink-soft/60 dark:placeholder-atlas-ink-soft-dark/60 font-mono text-lg tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-atlas-brass dark:focus:ring-atlas-brass-dark"
              />
            </div>
            <button
              onClick={() => onSubmitJoin(joinCode)}
              className="w-full h-[52px] rounded-xl bg-atlas-brass dark:bg-atlas-brass-dark hover:brightness-95 active:brightness-90 text-atlas-ink font-semibold text-[17px] transition-all"
            >
              Join Game
            </button>
            <button
              onClick={onBackFromJoin}
              className="text-sm font-medium text-atlas-ink-soft dark:text-atlas-ink-soft-dark hover:text-atlas-ink dark:hover:text-atlas-ink-dark text-center"
            >
              ← Back
            </button>
          </>
        )}

        {joinError && (
          <p className="text-sm text-atlas-brick dark:text-atlas-brick-dark text-center">{joinError}</p>
        )}
      </div>
    </AppScreen>
  );
}
